import React from 'react';
import { Box, Typography } from '@mui/material';
import { SparkDataTable } from 'sharedComponents/UI';
import { Column } from 'sharedComponents/UI/SparkDataTable';
import type { StripeInvoice } from 'types/Stripe/StripeInvoices';
import { statusConfigByStatusTag } from 'components/CreateSpark/SparkBuilderCanvas/ChartComponents/statusConfigByStatusTag';
import { formatDateTimeMonth } from 'utilities/time';

type BillingAndUsageRow = {
  id: string;
  paid: string;
  status: string;
  date: string;
  amount: string;
};

type BillingAndUsageInvoicesProps = {
  stripeInvoices: StripeInvoice[] | undefined;
};

function BillingAndUsageInvoices({
  stripeInvoices,
}: BillingAndUsageInvoicesProps) {
  const columns: Column<BillingAndUsageRow>[] = [
    {
      field: 'status',
      headerText: 'Status',
      sortable: true,
      cellRenderer: (row) => {
        const config = statusConfigByStatusTag[row.status];
        const Icon = config.Icon;
        return (
          <Box display='flex' alignItems='center' gap={2} flex={1}>
            <Icon />
            <Typography
              sx={{
                color: config.borderColor,
                textTransform: 'capitalize',
              }}
            >
              {row.status}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'date',
      headerText: 'Date',
      sortable: true,
      cellRenderer: (row) => (
        <Box display='flex' alignItems='center' gap={2} flex={1}>
          <Typography>{formatDateTimeMonth(+row.date * 1000)}</Typography>
        </Box>
      ),
    },
    {
      field: 'amount',
      headerText: 'Amount',
      sortable: true,
      cellRenderer: (row) => (
        <Box display='flex' alignItems='center' gap={2} flex={1}>
          <Typography>${row.amount}</Typography>
        </Box>
      ),
    },
  ];

  const rows: BillingAndUsageRow[] =
    stripeInvoices?.map((invoice) => {
      return {
        id: invoice.id,
        paid: invoice.paid ? 'Paid' : 'Pending',
        status: invoice.status,
        date: invoice.created,
        amount: `${(invoice.total / 100).toFixed(2)}`,
      };
    }) || [];

  const handleRowClick = (rowID: string) => {
    const selectedInvoice = stripeInvoices?.find(
      (invoice) => invoice.id === rowID
    );
    const navigateURL = selectedInvoice?.hosted_invoice_url;
    window.open(navigateURL);
  };

  return (
    <SparkDataTable<BillingAndUsageRow>
      columns={columns}
      rows={rows}
      title='Invoices'
      onRowClick={handleRowClick}
    />
  );
}

export default BillingAndUsageInvoices;
